import React, { useState, useEffect } from 'react';
import { Users, Star, Award, Sparkles, ArrowRight } from 'lucide-react';
import ApiService from '../services/api';

interface TeamMember {
  id: number;
  name: string;
  specialization: string;
  imageUrl?: string;
  displayOrder: number;
  isActive: boolean;
}

interface HomeSettings {
  id: number;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  isActive: boolean;
}

const Team: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [homeSettings, setHomeSettings] = useState<HomeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load team members
  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getTeamMembers();
      setTeamMembers(response.teamMembers || []);
    } catch (error: any) {
      console.error('Team members load error:', error);
      setError('Team members could not be loaded: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHomeSettings = async () => {
    try {
      const response = await ApiService.getHomeSettings();
      if (response && response.settings) {
        setHomeSettings(response.settings);
      }
    } catch (error) {
      console.error('Home settings load error:', error);
    }
  };

  useEffect(() => {
    loadTeamMembers();
    loadHomeSettings();
  }, []);

  // Get image URL
  const getImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
    }
    if (imageUrl.startsWith('/uploads/team/')) {
      const filename = imageUrl.replace('/uploads/team/', '');
      return `http://localhost:5000/api/proxy/uploads/team/${filename}`;
    }
    return imageUrl;
  };

  return (
    <div className="text-white min-h-screen" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-6xl md:text-8xl font-light mb-6 tracking-wider">
            EKİBİMİZ
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 font-light max-w-3xl mx-auto">
            Size en yüksek kalitede tırnak sanatı hizmetleri sunmaya adanmış deneyimli ve uzman ekibimizle tanışın. 
            Her birimiz kendi alanında uzmanlaşmış profesyonelleriz.
          </p>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-gray-400 font-light">Ekip üyeleri yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="bg-red-900/20 border border-red-600 text-red-400 px-4 py-3 rounded max-w-md mx-auto">
                <p>{error}</p>
              </div>
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h3 className="text-xl font-light text-gray-400 mb-2">Henüz Ekip Üyesi Yok</h3>
              <p className="text-gray-500">Ekip bilgileri yakında eklenecek</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-wider">
                  UZMAN EKİP
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto font-light">
                  Her profesyonelimiz kendi alanında uzmanlaşmış olup, 
                  size en yüksek kalitede hizmet sunar
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {teamMembers.map((member, index) => (
                  <div 
                    key={member.id} 
                    className="group border border-gray-800 hover:border-gray-600 transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
                    style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}
                  >
                    {/* Member Image */}
                    <div className="relative overflow-hidden">
                      <img
                        src={getImageUrl(member.imageUrl)}
                        alt={member.name}
                        className="w-full h-64 sm:h-72 md:h-80 object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>

                    {/* Member Info */}
                    <div className="p-6 md:p-8">
                      <div className="text-center">
                        <h3 className="text-xl md:text-2xl font-light text-white mb-3 tracking-wide">
                          {member.name}
                        </h3>
                        <p className="text-gray-400 font-light mb-4 text-sm md:text-base">
                          {member.specialization}
                        </p>
                        
                        {/* Decorative elements */}
                        <div className="flex justify-center space-x-1 mb-4">
                          <Star className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                          <Star className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                          <Star className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                          <Star className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                          <Star className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Why Choose Our Team Section */}
      <section className="py-20 px-4" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-wider">
              NEDEN EKİBİMİZİ SEÇMELİSİNİZ?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light">
              Profesyonel ekibimizin sunduğu avantajlar
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div className="text-center p-8 bg-gray-800 border border-gray-700 hover:border-gray-600 transition-all duration-300">
              <div className="bg-gray-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-light text-white mb-4 tracking-wide">
                UZMAN KADRO
              </h3>
              <p className="text-gray-400 text-sm md:text-base font-light">
                Her ekip üyesi kendi alanında uzmanlaşmış profesyonel olup sürekli eğitim almaktadır.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-800 border border-gray-700 hover:border-gray-600 transition-all duration-300">
              <div className="bg-gray-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-light text-white mb-4 tracking-wide">
                YARATICI TASARIMLAR
              </h3>
              <p className="text-gray-400 text-sm md:text-base font-light">
                Ekibimiz, her müşterinin özel isteklerini anlayarak yaratıcı ve benzersiz tasarımlar oluşturur.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-800 border border-gray-700 hover:border-gray-600 transition-all duration-300">
              <div className="bg-gray-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-light text-white mb-4 tracking-wide">
                KİŞİSEL İLGİ
              </h3>
              <p className="text-gray-400 text-sm md:text-base font-light">
                Her müşteriye özel ilgi göstererek en iyi deneyimi sağlamayı hedefliyoruz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-wider">
            EKİBİMİZLE TANIŞIN
          </h3>
          <p className="text-xl text-gray-300 mb-8 font-light">
            Profesyonel ekibimize randevu alın ve özel hissedin. 
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.location.href = '/contact'}
              className="inline-flex items-center px-8 py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 text-lg tracking-wide"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              RANDEVU AL
            </button>
            <a 
              href="tel:+905551234567" 
              className="inline-flex items-center px-8 py-4 border border-white text-white hover:bg-white hover:text-black transition-all duration-300 text-lg tracking-wide"
            >
              📞 ŞİMDİ ARA
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Sheva Studio. Tüm Hakları Saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Team;
